import { useEffect, useRef, useState, type PointerEvent } from "react";
import { itemQuantity, listItemName, type ListItem } from "../../domain/lists";

export function ListItemRow({ item, checkable = true, onCheck, onChange, onDelete, onEditingChange }: { item: ListItem; checkable?: boolean; onCheck: () => void; onChange?: (item: ListItem) => void; onDelete: () => void; onEditingChange?: (editing: boolean) => void }) {
  const [offset, setOffset] = useState(0);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(item.name);
  const gesture = useRef<{ id: number; x: number; y: number; base: number; horizontal: boolean | null } | null>(null);
  const longPressTimer = useRef<number | null>(null);
  const suppress = useRef(false);
  useEffect(() => setName(item.name), [item.id, item.name]);
  useEffect(() => () => { if (longPressTimer.current !== null) window.clearTimeout(longPressTimer.current); }, []);
  const clearLongPress = () => {
    if (longPressTimer.current !== null) window.clearTimeout(longPressTimer.current);
    longPressTimer.current = null;
  };
  const beginRename = () => {
    suppress.current = true;
    gesture.current = null;
    setOffset(0);
    setName(item.name);
    setRenaming(true);
    onEditingChange?.(true);
  };
  const start = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || renaming || (event.target as HTMLElement).closest("input, .list-rename-save, .list-trash")) return;
    suppress.current = false;
    gesture.current = { id: event.pointerId, x: event.clientX, y: event.clientY, base: offset, horizontal: null };
    clearLongPress();
    longPressTimer.current = window.setTimeout(() => {
      longPressTimer.current = null;
      beginRename();
    }, 520);
  };
  const move = (event: PointerEvent<HTMLDivElement>) => {
    const current = gesture.current;
    if (!current || current.id !== event.pointerId) return;
    const dx = event.clientX - current.x, dy = event.clientY - current.y;
    if (Math.max(Math.abs(dx), Math.abs(dy)) > 7) clearLongPress();
    if (current.horizontal === null && Math.max(Math.abs(dx), Math.abs(dy)) > 7) current.horizontal = Math.abs(dx) > Math.abs(dy);
    if (!current.horizontal) return;
    event.preventDefault(); suppress.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
    setOffset(Math.max(0, Math.min(82, current.base + dx)));
  };
  const finish = (event: PointerEvent<HTMLDivElement>) => {
    clearLongPress();
    const current = gesture.current;
    if (!current || current.id !== event.pointerId) {
      window.setTimeout(() => { suppress.current = false; }, 0);
      return;
    }
    gesture.current = null;
    if (current.horizontal) setOffset(event.type !== "pointercancel" && current.base + event.clientX - current.x >= 38 ? 82 : 0);
    window.setTimeout(() => { suppress.current = false; }, 0);
  };
  const saveName = () => {
    const next = listItemName(name);
    if (next && next !== item.name) onChange?.({ ...item, name: next });
    else setName(item.name);
    setRenaming(false);
    onEditingChange?.(false);
  };
  return <div className="list-swipe" onPointerDown={start} onPointerMove={move} onPointerUp={finish} onPointerCancel={finish} onContextMenu={event => { event.preventDefault(); if (!renaming) beginRename(); }}>
    <button className="list-trash" type="button" aria-label={`Удалить ${item.name}`} onFocus={() => setOffset(82)} onPointerDown={event => event.stopPropagation()} onClick={onDelete}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3m3 0-1 13H7L6 7m4 4v5m4-5v5" /></svg><span>Удалить</span></button>
    <div className={`list-item list-item-compact${item.status === "done" ? " is-done" : ""}${renaming ? " is-renaming" : ""}`} style={{ transform: `translateX(${offset}px)` }} onClickCapture={event => { if (suppress.current) { event.preventDefault(); event.stopPropagation(); } }}>
      {renaming ? <>
        <input className="list-inline-name" autoFocus aria-label={`Изменить ${item.name}`} value={name} maxLength={200} onChange={event => setName(event.target.value)} onKeyDown={event => {
          if (event.key === "Enter") { event.preventDefault(); saveName(); }
          if (event.key === "Escape") { event.preventDefault(); setName(item.name); setRenaming(false); onEditingChange?.(false); }
        }} />
        <button className="list-rename-save" type="button" aria-label="Сохранить название" disabled={!name.trim()} onClick={saveName}>✓</button>
      </> : checkable ? <button className="list-item-toggle" type="button" role="checkbox" aria-checked={item.status === "done"} aria-label={`${listItemName(item.name)}${itemQuantity(item) ? `, ${itemQuantity(item)}` : ""}`} onClick={onCheck}><span className="list-checkmark" aria-hidden="true">{item.status === "done" ? "✓" : ""}</span><span className="list-item-copy"><strong>{listItemName(item.name)}</strong>{itemQuantity(item) && <span>{itemQuantity(item)}</span>}</span></button> : <div className="list-item-copy"><strong>{listItemName(item.name)}</strong>{itemQuantity(item) && <span>{itemQuantity(item)}</span>}</div>}
    </div>
  </div>;
}
